// External modules
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone'
import { momentLocalizer } from 'react-big-calendar'
import {cloneDeep} from 'lodash'
import { RRule } from 'rrule'
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Store
import { getSelectedNodes, focusNodeRebootDetail } from '../../../store/positions/menuNodesSlice';
import { RebootDetailStyle } from '../../../store/positions/types';

// Internal components
import { EditPanel } from './EditPanel';
import { CustomEvent, CustomEventWrapper } from './Calendar';

// Common fields 
import { StandardMainDisplayMessage } from '../../common/fields/devices/DisplayMessages';
import { messages_common, messages_reboot } from '../../common/types/Messages';
import { Header, Toolbar } from '../../common/fields/devices/Calendar';

// Styled components
import { StyledContainer, StyledMiddleContainer, StyledRightContainer } from '../../common/styles/devices/Container';
import { StyledBigCalendar } from '../../common/styles/devices/Calendar';

export function WeeklyRestart({ path: string }) {
   
  const dispatch = useDispatch();
  const selectedNodes = useSelector(getSelectedNodes);  

  const focusedRebootDetail = selectedNodes.length? selectedNodes[0].focus : null;
  var has_reboot_details = true;
  
  let calendar_items_recurring = [];  
  
  selectedNodes.forEach((item) => {
    if (item.dtstart == null || item.byweekday == null) has_reboot_details = false;
    else{
      const rebootDetail = new RRule({
        freq: RRule.WEEKLY,      
        byweekday: item.byweekday,
        dtstart: new Date(moment.tz(item.dtstart, 'UTC').valueOf()),      
      });
      
      const weekstart = moment().startOf('isoWeek').subtract(1, 'week');
      const weekend = moment().endOf('isoWeek');
      
      let occurences = rebootDetail.between(new Date(weekstart.valueOf()), new Date(weekend.valueOf()));
      
      occurences.forEach((dtstart, index) => {
        let cloned_item :any = cloneDeep(item);
        
        cloned_item.dtstart = new Date(moment(moment(dtstart).utc().format('YYYY-MM-DD HH:mm:ss')).valueOf());     
        
        // React big calendar has a bug (IMO) and fails to display periods that 
        // end at midnight. The end is change to 23:59 to get around this for now
        let end = moment(cloned_item.dtstart.valueOf() + ((1*3600) * 1000) );
        if (end.format('HH:mm:ss') === '00:00:00') {
          end.subtract(1, 'minute');
        }
        
        cloned_item.end = end.toDate();      
        calendar_items_recurring.push(cloned_item);
      })
    }    

  });  
  
  moment.locale('gb', {
      week: {
          dow: 1
      },
  });

  const localizer = momentLocalizer(moment) // or globalizeLocalizer
  
  let formats = {
    timeGutterFormat: 'HH:mm'
  }
   
  function setFocusBorders(props) {
    
    const style: RebootDetailStyle = {background: props.style.background}
    let line_type = 'solid'
    
    if (!props.saved) {
      line_type = `dashed`;
    }
    
    if (props.focus) {      
      if (props.saved) {        
        style.border = `3px solid  ${props.style.border}`;   
      } else {
        style.border = `3px dashed  ${props.style.border}`;
      }
    } else {
      if (props.saved) {
        style.border = `1px solid  ${props.style.border}`;
      } else {
        style.border = `2px dashed  ${props.style.border}`;
      }
    }   
    
    return {style: style}
  }
  // Checks if a station has the capability attribute
  const has_capability = selectedNodes.length > 0 && selectedNodes[0].weekly_reboot_enabled !== null;  
  
  // Check if the type of instrument is not a vibra station
  const has_vibra_type = selectedNodes.length > 0 && !(selectedNodes[0].instrument_type == 2 && (selectedNodes[0].instrument_subtype == 0)); 
  
  // Check if the type of instrument is not a met and co located station
  const has_colocated_met_type = selectedNodes.length > 0 && !(selectedNodes[0].instrument_type == 4 && selectedNodes[0].co_located_id !== null) ;  
  
  

  return (
      <StyledContainer>
        <StyledMiddleContainer>       
          {selectedNodes.length == 0 ? <StandardMainDisplayMessage message={messages_common.select}/> : null} 
          {selectedNodes.length > 0 && !has_capability && has_vibra_type && has_colocated_met_type ? <StandardMainDisplayMessage message={messages_common.firmware}/> : null}  
          {selectedNodes.length > 0 && !has_vibra_type ? <StandardMainDisplayMessage message={messages_reboot.no_service}/> : selectedNodes.length > 0 && !has_colocated_met_type ? <StandardMainDisplayMessage message={messages_reboot.co_located}/> : null}   
          {selectedNodes.length > 0 && has_capability && has_vibra_type && has_colocated_met_type && !has_reboot_details? <StandardMainDisplayMessage message={messages_reboot.disabled}/> : null }
          {selectedNodes.length > 0 && has_capability && has_vibra_type && has_colocated_met_type && has_reboot_details? 
            <StyledBigCalendar
              padding='75px 5% 20px 5%'
              min_height='758px'
              localizer={localizer}
              events={calendar_items_recurring}
              startAccessor="dtstart"
              endAccessor="end"
              defaultView='week'
              showMultiDayTimes={true}
              step={60}
              timeslots={1}
              formats={formats}
              resizable={true}               
              popup={true}
              components={{
                day: { header: Header },
                week: { header: Header },
                month: { header: Header },
                toolbar: Toolbar,
                event: CustomEvent,
                eventWrapper: CustomEventWrapper
              }}            
              selectable={false}                
              onSelectEvent={(event, e) => dispatch(focusNodeRebootDetail({id: event.id}))}    
              eventPropGetter={setFocusBorders}        
            />
          : null}
        </StyledMiddleContainer>
        <StyledRightContainer>
          {selectedNodes.length > 0 && has_capability && has_vibra_type && has_colocated_met_type && has_reboot_details?
          <EditPanel node={selectedNodes[0]} />
          : null}
        </StyledRightContainer>
      </StyledContainer>
  );
}

